import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "./Footer";
import SEO from "./SEO";
import { brand } from "../mock";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_sterling-landing/artifacts/kxq2bn2y_The%20Sterling%20Touch%20Logo%20%289%29.png";

/**
 * Shared layout for static legal pages (Privacy Policy, T&Cs).
 * Keeps the same header rhythm as the rest of the marketing site but
 * presents long-form copy in a tight reading column.
 */
const LegalLayout = ({
  eyebrow,
  title,
  lastUpdated,
  intro,
  children,
  seoTitle,
  seoDescription,
  path,
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <SEO title={seoTitle} description={seoDescription} path={path} />

      <header
        className="sticky top-0 z-40 bg-cream/95 backdrop-blur border-b border-[#e8d9cc]"
        data-testid="legal-header"
      >
        <div className="max-w-5xl mx-auto px-6 h-24 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="flex items-center gap-2 text-ink hover:text-[#b8866e] transition-colors shrink-0"
          >
            <ArrowLeft size={18} />
            <span className="hidden sm:inline text-sm">Back to home</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="legal-brand-link"
          >
            <img
              src={LOGO_URL}
              alt={brand.name}
              className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
            />
            <div className="leading-tight text-right sm:text-left">
              <div className="font-brand text-base md:text-lg text-ink">
                {brand.name}
              </div>
              <div className="text-[11px] sm:text-xs italic text-[#b8866e] mt-1">
                {brand.tagline}
              </div>
            </div>
          </Link>
        </div>
      </header>

      <main className="flex-1 px-6 py-16 lg:py-24" data-testid="legal-main">
        <div className="max-w-3xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-[#daae9c]">
            {eyebrow}
          </div>
          <h1
            className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-ink mt-4 leading-[1.05] text-balance"
            data-testid="legal-title"
          >
            {title}
          </h1>
          {lastUpdated && (
            <p className="mt-4 text-sm text-ink/50">Last updated: {lastUpdated}</p>
          )}
          {intro && (
            <p className="mt-6 text-lg text-ink/75 leading-relaxed">{intro}</p>
          )}

          <article
            className="legal-prose mt-12 space-y-10 text-ink/80 leading-relaxed"
            data-testid="legal-body"
          >
            {children}
          </article>

          <div className="mt-16 pt-8 border-t border-[#ead8c9] flex flex-wrap items-center justify-between gap-4 text-sm">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-ink hover:text-[#b8866e] transition-colors"
              data-testid="legal-bottom-back-home"
            >
              <ArrowLeft size={14} /> Back to home
            </Link>
            <div className="flex items-center gap-6 text-ink/60">
              <Link
                to="/privacy-policy"
                className="hover:text-ink transition-colors"
                data-testid="legal-link-privacy"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-and-conditions"
                className="hover:text-ink transition-colors"
                data-testid="legal-link-terms"
              >
                Terms &amp; Conditions
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export const LegalSection = ({ heading, children }) => (
  <section>
    <h2 className="font-serif-display text-2xl md:text-3xl text-ink leading-snug">
      {heading}
    </h2>
    <div className="mt-4 space-y-4 text-base">{children}</div>
  </section>
);

export default LegalLayout;
